import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { assertWritable } from "@/lib/viewAs";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { CLIENT_NAV } from "@/lib/client-nav";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  Folder,
  FileText,
  Image as ImageIcon,
  Download,
  Trash2,
  UploadCloud,
} from "lucide-react";

export const Route = createFileRoute("/client/vault")({
  head: () => ({
    meta: [
      { title: "Family Vault — Currency Thoughts" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: VaultPage,
});

const CATEGORIES = ["Policies", "Trusts & Wills", "Beneficiaries", "Tax Documents", "Other"] as const;
type Category = (typeof CATEGORIES)[number];

const MAX_BYTES = 25 * 1024 * 1024;
const ACCEPT = {
  "application/pdf": [".pdf"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
};

interface Doc {
  id: string;
  name: string;
  path: string;
  bucket: string;
  mime_type: string | null;
  created_at: string;
  category?: string | null;
}

function categoryFromPath(path: string): string {
  const parts = path.split("/");
  return parts.length >= 3 ? parts[1] : "Other";
}

function VaultPage() {
  return (
    <ProtectedRoute requiredRole="client">
      <PortalShell title="Family Vault" nav={CLIENT_NAV}>
        <Vault />
      </PortalShell>
    </ProtectedRoute>
  );
}

function Vault() {
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState<Category>("Policies");
  const [filter, setFilter] = useState<"All" | Category>("All");
  const [docs, setDocs] = useState<Doc[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("documents")
      .select("id, name, path, bucket, mime_type, created_at")
      .eq("owner_id", user.id)
      .eq("bucket", "client-uploads")
      .order("created_at", { ascending: false });
    setDocs(((data ?? []) as Doc[]).map((d) => ({ ...d, category: categoryFromPath(d.path) })));
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const onDrop = useCallback(
    async (files: File[]) => {
      if (!user || files.length === 0) return;
      setBusy(true);
      try {
        for (const file of files) {
          if (file.size > MAX_BYTES) {
            toast.error(`${file.name} exceeds 25 MB`);
            continue;
          }
          const path = `${user.id}/${activeCategory}/${Date.now()}-${file.name}`;
          const { error: upErr } = await supabase.storage
            .from("client-uploads")
            .upload(path, file, { contentType: file.type, upsert: false });
          if (upErr) {
            toast.error(`Upload failed: ${file.name}`);
            continue;
          }
          await supabase.from("documents").insert({
            owner_id: user.id,
            bucket: "client-uploads",
            path,
            name: file.name,
            mime_type: file.type,
            size: file.size,
          });
          await supabase.from("activity_logs").insert({
            user_id: user.id,
            action: "document.uploaded",
            target_type: "document",
            target_id: path,
          });
        }
        toast.success("Documents secured in your vault");
        await load();
      } finally {
        setBusy(false);
      }
    },
    [user, activeCategory, load],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT,
    maxSize: MAX_BYTES,
  });

  const download = async (d: Doc) => {
    const { data, error } = await supabase.storage
      .from(d.bucket)
      .createSignedUrl(d.path, 60 * 60);
    if (error || !data) return toast.error("Could not generate link");
    window.open(data.signedUrl, "_blank");
  };

  const remove = async (d: Doc) => {
    if (!assertWritable()) return;
    if (!confirm(`Permanently delete "${d.name}"?`)) return;
    await supabase.storage.from(d.bucket).remove([d.path]);
    await supabase.from("documents").delete().eq("id", d.id);
    await supabase.from("activity_logs").insert({
      user_id: user?.id,
      action: "document.deleted",
      target_type: "document",
      target_id: d.path,
    });
    toast.success("Document removed");
    await load();
  };

  const visible = filter === "All" ? docs : docs.filter((d) => d.category === filter);

  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="font-display font-semibold text-navy-800" style={{ fontSize: "36px", letterSpacing: "-0.01em" }}>
        Family <em className="text-bronze-600 italic font-normal">Vault</em>
      </h2>
      <p className="mt-2 font-sans text-[15px] text-charcoal-500 max-w-2xl">
        Secure storage for the documents that matter most.
      </p>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setActiveCategory(c)}
            className={`group bg-white rounded-[4px] p-5 border transition-all text-left ${
              activeCategory === c
                ? "border-bronze-600 shadow-bronze-sm"
                : "border-bronze-600/20 hover:border-bronze-400"
            }`}
          >
            <Folder
              size={28}
              className={activeCategory === c ? "text-bronze-600" : "text-navy-800 group-hover:text-bronze-400"}
              fill={activeCategory === c ? "currentColor" : "none"}
            />
            <p className="mt-3 font-display text-navy-800 text-[15px]">{c}</p>
            <p className="mt-0.5 font-sans text-[11px] text-charcoal-500">
              {docs.filter((d) => d.category === c).length} files
            </p>
          </button>
        ))}
      </div>

      <div
        {...getRootProps()}
        className={`mt-8 border-2 border-dashed rounded-[8px] p-12 text-center cursor-pointer transition-colors ${
          isDragActive ? "border-bronze-400 bg-bronze-400/10" : "border-bronze-600 bg-white/40"
        }`}
      >
        <input {...getInputProps()} />
        <UploadCloud size={32} className="mx-auto text-bronze-600" />
        <p className="mt-3 font-sans text-[14px] text-charcoal-900">
          {busy ? "Uploading…" : "Drop files here or click to upload"}
        </p>
        <p className="mt-1 font-sans text-[11px] text-charcoal-500">
          PDF, PNG, JPG, DOCX · max 25 MB · uploading to <strong className="text-bronze-600">{activeCategory}</strong>
        </p>
      </div>

      <div className="mt-10">
        <div className="flex flex-wrap gap-2">
          {(["All", ...CATEGORIES] as const).map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-3 py-1.5 rounded-full font-sans text-[11px] uppercase tracking-[0.12em] transition-colors ${
                filter === c
                  ? "bg-bronze-600 text-navy-900"
                  : "bg-bronze-600/10 text-bronze-600 hover:bg-bronze-600/20"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-5 bg-white border border-bronze-600/20 rounded-[4px] overflow-hidden">
          <table className="w-full">
            <thead className="bg-cream-50 border-b border-bronze-600/20">
              <tr>
                <th className="text-left p-3 font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">File</th>
                <th className="text-left p-3 font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">Category</th>
                <th className="text-left p-3 font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">Uploaded</th>
                <th className="text-right p-3 font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center font-sans text-[13px] text-charcoal-500">
                    No documents in this view.
                  </td>
                </tr>
              )}
              {visible.map((d) => {
                const isImg = d.mime_type?.startsWith("image/");
                const Icon = isImg ? ImageIcon : FileText;
                return (
                  <tr key={d.id} className="border-b border-bronze-600/10 last:border-0">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Icon size={15} className="text-bronze-600 shrink-0" />
                        <span className="font-sans text-[13px] text-charcoal-900">{d.name}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 bg-bronze-600/15 text-bronze-600 font-sans text-[10px] uppercase tracking-[0.12em] rounded-full">
                        {d.category}
                      </span>
                    </td>
                    <td className="p-3 font-sans text-[12px] text-charcoal-500">
                      {new Date(d.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => void download(d)} className="text-charcoal-500 hover:text-navy-800 mr-3" title="Download">
                        <Download size={15} />
                      </button>
                      <button onClick={() => void remove(d)} className="text-charcoal-500 hover:text-danger" title="Delete">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}